import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Shield } from "lucide-react";

interface ATSSystem {
  system: string;
  score: number;
  issues: string[];
}

interface ATSSystemCompatibilityProps {
  systems: ATSSystem[];
}

export const ATSSystemCompatibility = ({ systems }: ATSSystemCompatibilityProps) => {
  const getScoreColor = (score: number) => {
    if (score >= 90) return "text-green-600";
    if (score >= 75) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBadge = (score: number) => {
    if (score >= 90) return { variant: "default" as const, label: "Excellent" };
    if (score >= 75) return { variant: "secondary" as const, label: "Good" };
    return { variant: "destructive" as const, label: "Needs Work" };
  };

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <h3 className="text-xl font-semibold mb-2">ATS System Compatibility</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Your resume's compatibility with major Applicant Tracking Systems used by employers.
        </p>
        
        <div className="space-y-6">
          {systems.map((system, index) => {
            const badge = getScoreBadge(system.score);
            
            return (
              <div key={index} className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-muted-foreground" />
                    <span className="font-semibold">{system.system}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                    <span className={`text-2xl font-bold ${getScoreColor(system.score)}`}>
                      {system.score}
                    </span>
                  </div>
                </div>
                
                <Progress value={system.score} className="h-2" />
                
                {system.issues.length > 0 && (
                  <div className="ml-8 space-y-2">
                    <p className="text-sm font-medium">Specific Issues for this ATS:</p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {system.issues.map((issue, issueIndex) => (
                        <li key={issueIndex} className="flex items-start gap-2">
                          <span className="text-destructive mt-0.5">•</span>
                          <span>{issue}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-6 bg-muted/50">
        <h4 className="font-semibold mb-3">About These ATS Systems</h4>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium mb-1">Workday</p>
            <p className="text-muted-foreground text-xs">Strict formatting, struggles with tables/graphics</p>
          </div>
          <div>
            <p className="font-medium mb-1">Taleo (Oracle)</p>
            <p className="text-muted-foreground text-xs">Very strict, basic parsing only</p>
          </div>
          <div>
            <p className="font-medium mb-1">Greenhouse</p>
            <p className="text-muted-foreground text-xs">Modern, better parsing, but still has limits</p>
          </div>
          <div>
            <p className="font-medium mb-1">iCIMS</p>
            <p className="text-muted-foreground text-xs">Struggles with creative formatting</p>
          </div>
          <div>
            <p className="font-medium mb-1">Lever</p>
            <p className="text-muted-foreground text-xs">Better with modern resumes</p>
          </div>
          <div>
            <p className="font-medium mb-1">SuccessFactors (SAP)</p>
            <p className="text-muted-foreground text-xs">Mid-range parsing ability</p>
          </div>
        </div>
      </Card>
    </div>
  );
};
